import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Loader2, TrendingUp, Users, Building2, Target, MapPin, ShieldAlert, Lightbulb, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : "-";
const fmtNum = (n) => n != null ? new Intl.NumberFormat("it-IT").format(n) : "-";

const confidenceColor = { alta: "bg-green-100 text-green-700", media: "bg-amber-100 text-amber-700", bassa: "bg-red-100 text-red-700" };
const saturationColor = { bassa: "bg-green-100 text-green-700", media: "bg-amber-100 text-amber-700", alta: "bg-red-100 text-red-700" };
const difficultyColor = { bassa: "bg-green-100 text-green-700", media: "bg-amber-100 text-amber-700", alta: "bg-red-100 text-red-700" };

export const PotentialDemandPage = ({ clientId, client }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load cached analysis on mount
  useEffect(() => {
    axios.get(`${API}/clients/${clientId}/potential-demand`)
      .then(res => {
        if (res.data.cached) {
          setData(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setInitialLoading(false));
  }, [clientId]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/potential-demand`);
      setData(res.data);
      toast.success("Analisi domanda potenziale completata e salvata");
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const missingFields = !client?.city && !client?.province;

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-600" /> Domanda Potenziale
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          {initialLoading ? (
            <Loader2 className="h-8 w-8 animate-spin text-slate-300 mx-auto" />
          ) : (
            <>
              <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-4" />
              <p className="text-sm text-slate-600 mb-2">
                Stima la domanda potenziale del mercato in base al profilo del cliente, dati ISTAT e analisi del territorio.
              </p>
              {missingFields && (
                <p className="text-xs text-amber-600 mb-3">
                  Per un'analisi piu precisa, compila Citta e Provincia nell'anagrafica del cliente.
                </p>
              )}
              <Button onClick={generate} disabled={loading} data-testid="generate-demand-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analisi in corso (30-60 sec)...</> : "Genera Analisi Domanda"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const pop = data.population_data || {};
  const biz = data.business_data || {};
  const dem = data.demand_estimate || {};
  const segments = data.target_segments || [];
  const landscape = data.competitive_landscape || {};
  const opportunities = data.opportunities || [];
  const risks = data.risks || [];
  const recommendations = data.recommendations || [];

  return (
    <div className="space-y-4" data-testid="potential-demand-page">
      {/* Summary */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-indigo-600" /> Domanda Potenziale
            </CardTitle>
            <div className="flex items-center gap-2">
              {data.generated_at && <span className="text-[10px] text-slate-400">Generata il {new Date(data.generated_at).toLocaleDateString('it-IT')}</span>}
              <Button variant="outline" size="sm" onClick={generate} disabled={loading} data-testid="regenerate-demand-btn">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Rigenera"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700">{data.summary}</p>
        </CardContent>
      </Card>

      {/* KPIs Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">TAM (Mercato Totale)</p>
            <p className="text-lg font-bold text-indigo-700">{fmtEur(dem.total_addressable_market)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">SAM (Mercato Servibile)</p>
            <p className="text-lg font-bold text-blue-700">{fmtEur(dem.serviceable_market)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Tasso Cattura Realistico</p>
            <p className="text-lg font-bold text-emerald-700">{dem.realistic_capture_rate_pct}%</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Fatturato Potenziale Annuo</p>
            <p className="text-lg font-bold text-amber-700">{fmtEur(dem.estimated_annual_revenue_potential)}</p>
            <Badge className={`text-[9px] mt-1 ${confidenceColor[dem.confidence_level] || ''}`}>{dem.confidence_level}</Badge>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Population / Business Data */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              {(client?.target_market === 'b2b') ? <Building2 className="h-4 w-4" /> : <Users className="h-4 w-4" />}
              {(client?.target_market === 'b2b') ? 'Tessuto Imprenditoriale' : 'Dati Demografici'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pop.area_name && (
              <div className="flex items-center gap-1 mb-2">
                <MapPin className="h-3 w-3 text-slate-400" />
                <span className="text-xs font-medium text-slate-600">{pop.area_name}</span>
              </div>
            )}
            <Row label="Popolazione" value={fmtNum(pop.total_population)} />
            <Row label="Nuclei familiari" value={fmtNum(pop.households)} />
            <Row label="Popolazione in eta lavorativa" value={fmtNum(pop.working_age_population)} />
            <Row label="Reddito medio pro-capite" value={fmtEur(pop.avg_income_per_capita)} />
            <Row label="Trend demografico" value={
              <span className={pop.population_trend === 'crescita' ? 'text-green-600' : pop.population_trend === 'declino' ? 'text-red-600' : 'text-slate-600'}>
                {pop.population_trend} ({pop.trend_pct > 0 ? '+' : ''}{pop.trend_pct}%)
              </span>
            } />
            {biz.total_businesses_in_area > 0 && (
              <>
                <div className="border-t my-2" />
                <Row label="Imprese nell'area" value={fmtNum(biz.total_businesses_in_area)} />
                <Row label="Imprese nel settore" value={fmtNum(biz.businesses_in_sector)} />
                <Row label="Potenziali clienti business" value={fmtNum(biz.businesses_potential_clients)} />
                <Row label="Spesa media per servizio" value={fmtEur(biz.avg_business_spending_on_service)} />
              </>
            )}
            <p className="text-[9px] text-slate-400 mt-2">{pop.source}</p>
          </CardContent>
        </Card>

        {/* Target Segments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4" /> Segmenti Target
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {segments.map((s, i) => (
              <div key={i} className="p-2 rounded border bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold">{s.name}</span>
                  <Badge className={`text-[9px] ${s.penetration_potential === 'alta' ? 'bg-green-100 text-green-700' : s.penetration_potential === 'media' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {s.penetration_potential}
                  </Badge>
                </div>
                <p className="text-[10px] text-slate-500">{s.description}</p>
                <div className="flex gap-3 mt-1">
                  <span className="text-[10px] text-slate-400">Dimensione: <b>{fmtNum(s.size)}</b></span>
                  <span className="text-[10px] text-slate-400">Spesa media: <b>{fmtEur(s.avg_spend)}</b></span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Competitive Landscape */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" /> Panorama Competitivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Row label="Competitor stimati nell'area" value={landscape.estimated_competitors_in_area} />
            <Row label="Saturazione mercato" value={
              <Badge className={`text-[9px] ${saturationColor[landscape.market_saturation] || ''}`}>{landscape.market_saturation}</Badge>
            } />
            {landscape.competitive_advantage_areas?.length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] text-slate-400 mb-1">Aree di vantaggio competitivo:</p>
                <div className="flex flex-wrap gap-1">
                  {landscape.competitive_advantage_areas.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-[9px]">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Opportunities */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Lightbulb className="h-4 w-4" /> Opportunita
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {opportunities.map((o, i) => (
              <div key={i} className="p-2 rounded border bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold">{o.title}</span>
                  <Badge className={`text-[9px] ${difficultyColor[o.difficulty] || ''}`}>{o.difficulty}</Badge>
                </div>
                <p className="text-[10px] text-slate-500">{o.description}</p>
                <div className="flex gap-3 mt-1">
                  <span className="text-[10px] text-slate-400">Valore: <b>{fmtEur(o.potential_value)}</b></span>
                  <span className="text-[10px] text-slate-400">Tempistica: <b>{o.timeframe}</b></span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Risks and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-red-500" /> Rischi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {risks.map((r, i) => (
              <div key={i} className="p-2 rounded border bg-red-50/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold">{r.title}</span>
                  <Badge className="text-[9px] bg-red-100 text-red-700">{r.impact}</Badge>
                </div>
                <p className="text-[10px] text-slate-500">{r.description}</p>
                <p className="text-[10px] text-blue-600 mt-1">Mitigazione: {r.mitigation}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" /> Raccomandazioni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {recommendations.map((r, i) => (
                <li key={i} className="text-xs text-slate-700 flex gap-2">
                  <span className="text-emerald-500 font-bold shrink-0">{i + 1}.</span>
                  {r}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Methodology */}
      {dem.methodology && (
        <Card>
          <CardContent className="py-3">
            <p className="text-[10px] text-slate-400"><b>Metodologia:</b> {dem.methodology}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center text-xs">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-700">{value}</span>
  </div>
);
